/**
 * TanStack Query Provider
 *
 * Provides query client context for the application.
 * Includes development-only devtools for debugging.
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 1 minute
      staleTime: 60 * 1000,

      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,

      // Retry failed requests once
      retry: 1,

      // Refetch on window focus (good for stale data)
      refetchOnWindowFocus: true,

      // Don't refetch on mount if data exists
      refetchOnMount: false,
    },
    mutations: {
      // Don't retry failed mutations
      retry: false,
    },
  },
});

interface Props {
  children: React.ReactNode;
}

/**
 * Wrap your app with this provider to enable TanStack Query
 *
 * @example
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 */
export function QueryProvider({ children }: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Uncomment to enable devtools in development */}
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
}

/**
 * Export the query client for direct access if needed
 * (e.g., in sagas or outside React components)
 */
export { queryClient };

export default QueryProvider;
